import app from 'flarum/forum/app';
import { extend, override } from 'flarum/common/extend';
import Model from 'flarum/common/Model';
import User from 'flarum/common/models/User';
import UserPage from 'flarum/forum/components/UserPage';
import UserControls from 'flarum/forum/utils/UserControls';
import LinkButton from 'flarum/common/components/LinkButton';
import Button from 'flarum/common/components/Button';
import BlockedProfilePanel from './components/BlockedProfilePanel';
import BlockedUsersPage from './components/BlockedUsersPage';
import SeparatedProfilePanel from './components/SeparatedProfilePanel';
import SeparateUsersModal from './components/SeparateUsersModal';

app.initializers.add('happydigital-ghost-block', () => {
  User.prototype.ghostBlocked = Model.attribute('ghostBlocked');
  User.prototype.ghostBlockedBy = Model.attribute('ghostBlockedBy');
  // Moderator-imposed separation — the users can't lift it themselves.
  User.prototype.ghostSeparated = Model.attribute('ghostSeparated');
  // FoF Ignore's attribute — Flarum pushes it to the store optimistically
  // the moment the ignore button is clicked, so reading it here makes the
  // blocked view appear instantly without waiting for a server round-trip.
  User.prototype.ghostIgnored = Model.attribute('ignored');

  // Only ever present on the logged-in user (see AddBlockLimitAttributes).
  User.prototype.ghostBlockLimit = Model.attribute('ghostBlockLimit');
  User.prototype.ghostBlockUsed = Model.attribute('ghostBlockUsed');
  User.prototype.ghostBlockRemaining = Model.attribute('ghostBlockRemaining');

  // Own-profile tab listing blocked users, next to Posts / Discussions / Likes.
  app.routes['user.ghostBlocked'] = { path: '/blocked', component: BlockedUsersPage };

  // A block/unblock changes what nearly every cached list (discussion list,
  // open post streams, search) is allowed to contain. Instead of chasing and
  // purging each cache, reload the page once the save succeeds — this also
  // covers blocks made through FoF Ignore's own button.
  override(User.prototype, 'save', function (original, attributes, ...args) {
    const result = original(attributes, ...args);

    if (attributes && 'ignored' in attributes) {
      return result
        .then((user) => {
          window.location.reload();
          return user;
        })
        .catch((error) => {
          // The backend denies the block once the actor's group limit is
          // reached (see BlockLimitPolicy). Surface that as a clear alert
          // instead of FoF's generic permission-denied message.
          if (attributes.ignored && error?.status === 403) {
            app.alerts.show(
              { type: 'error' },
              app.translator.trans('happydigital-ghost-block.forum.profile.limit_reached')
            );
          }

          throw error;
        });
    }

    return result;
  });

  // Replace the whole profile page between blocked pairs:
  // only the username stays visible.
  override(UserPage.prototype, 'view', function (original, ...args) {
    const user = this.user;

    // Checked first: a separation overrides any personal block, and must
    // never offer an unblock button.
    if (user && user.ghostSeparated()) {
      return <SeparatedProfilePanel user={user} />;
    }

    if (user && (user.ghostBlocked() || user.ghostBlockedBy() || user.ghostIgnored())) {
      return <BlockedProfilePanel user={user} />;
    }

    return original(...args);
  });

  // FoF's own ignore button disappears once `canBeIgnored` is false. When
  // that's because the actor hit their block limit (BlockLimitPolicy), show
  // a disabled hint instead of letting the button silently vanish.
  extend(UserControls, 'userControls', function (items, user) {
    const actor = app.session.user;

    if (!actor || actor === user) return;

    // Separated by a moderator: no ignore / unignore control at all.
    if (user.ghostSeparated()) {
      if (items.has('ignore')) items.remove('ignore');
      return;
    }

    if (user.ghostIgnored()) return;

    const remaining = actor.ghostBlockRemaining();
    const limitKnown = remaining !== undefined && remaining !== null;

    if (typeof user.canBeIgnored === 'function' && !user.canBeIgnored() && limitKnown && remaining <= 0) {
      items.add(
        'ghost-block-limit-reached',
        <Button className="Button" icon="fas fa-lock" disabled title={app.translator.trans('happydigital-ghost-block.forum.profile.limit_reached')}>
          {app.translator.trans('happydigital-ghost-block.forum.profile.limit_reached_button')}
        </Button>,
        -10
      );
    }
  });

  extend(UserControls, 'moderationControls', function (items, user) {
    const actor = app.session.user;

    if (!actor || actor === user || !app.forum.attribute('canGhostSeparateUsers')) return;

    items.add(
      'ghost-separate',
      <Button icon="fas fa-people-arrows" onclick={() => app.modal.show(SeparateUsersModal, { user })}>
        {app.translator.trans('happydigital-ghost-block.forum.separate.control_button')}
      </Button>
    );
  });

  extend(UserPage.prototype, 'navItems', function (items) {
    if (!app.session.user || app.session.user !== this.user) return;

    // FoF Ignore's own "Ignored Users" tab is superseded by ours.
    // FoF boots before us (it's our composer dependency), so its item
    // is already in the list at this point.
    if (items.has('ignored-users')) items.remove('ignored-users');

    items.add(
      'ghost-blocked',
      <LinkButton href={app.route('user.ghostBlocked')} icon="fas fa-ghost">
        {app.translator.trans('happydigital-ghost-block.forum.blocked_list.nav_button')}
      </LinkButton>,
      70
    );
  });
});
